import React from "react";
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider } from "react-router-dom";
import QuoteApprovalListForm from "../../pages-nozod/QuoteApprovalList/QuoteApprovalListForm";
import QuoteDetails from "../../pages-nozod/QuoteDetails/QuoteDetails";
import SigninForm from "../../pages-nozod/Signin/SigninForm";
import LoginForm from "../../pages-nozod/Login/LoginForm";
import QuoteApprovalReassignmentListForm from "../../pages-nozod/QuoteApprovalReassignmentList/QuoteApprovalReassignmentListForm";
import CreateQuoteApprovalReassignmentForm from "../../pages-nozod/CreateQuoteApprovalReassignment/CreateQuoteApprovalReassignmentForm";
import Home from "../../pages-nozod/Home/Home";
import {
  withEmployeeRequired,
  withUserRequired,
  withUserRoleRequired,
  withSuperAdminRoleRequired,
  withContractAuthManagerRoleRequired,
  withEitherUserRoleOrPricingApprover,
} from "../../hoc/withAuthRedirect";

import PricingConfigurationListForm from "../../pages-nozod/PricingConfigurationList/PricingConfigurationListForm";
import { useSelector } from "react-redux";
import { selectEmployee, selectUser } from "../../store/store-selector";
import CreateUserRoleForm from "../../pages-nozod/CreateUserRole/CreateUserRoleForm";
import UserRoleListForm from "../../pages-nozod/UserRoleList/UserRoleListForm";
import AuthorisedSignatureConfigListForm from "../../pages-nozod/AuthorisedSignatureConfigList/AuthorisedSignatureConfigListForm";
import CreateAuthSignConfigForm from "../../pages-nozod/CreateAuthSignConfig/CreateAuthSignConfigForm";

import * as AppPages from "../../pages";

const PageRoute = () => {
  const user = useSelector(selectUser);
  const employee = useSelector(selectEmployee);

  const ProtectedLoginForm = withUserRequired(LoginForm);
  const ProtectedQuoteApprovalListForm = withEmployeeRequired(QuoteApprovalListForm);
  const ProtectedQuoteDetails = withEmployeeRequired(QuoteDetails);
  const ProtectedHome = withEmployeeRequired(Home);
  const ProtectedDeviceListForm = withUserRoleRequired(AppPages.DeviceGroupView, { displayList: true });
  const ProtectedCreateDeviceForm = withUserRoleRequired(AppPages.DeviceGroupView, { displayList: false });
  const ProtectedZoneListForm = withUserRoleRequired(AppPages.ZoneView, { displayList: true });
  const ProtectedCreateZoneForm = withUserRoleRequired(AppPages.ZoneView, { displayList: false });
  const ProtectedPezodeListForm = withUserRoleRequired(AppPages.PezodeGroupView, { displayList: true });
  const ProtectedCreatePezodeForm = withUserRoleRequired(AppPages.PezodeGroupView, { displayList: false });
  const ProtectedDeviceTypeListForm = withUserRoleRequired(AppPages.DeviceTypeView, { displayList: true });
  const ProtectedCreateDeviceTypeForm = withUserRoleRequired(AppPages.DeviceTypeView, { displayList: false });
  const ProtectedConsumableProductListForm = withUserRoleRequired(AppPages.ConsumableProductGroupView, { displayList: true });
  const ProtectedCreateConsumableProductForm = withUserRoleRequired(AppPages.ConsumableProductGroupView, { displayList: false });
  const ProtectedServiceTypeListForm = withUserRoleRequired(AppPages.ServiceTypeView, { displayList: true });
  const ProtectedCreateServiceTypeForm = withUserRoleRequired(AppPages.ServiceTypeView, { displayList: false });
  const ProtectedMarketSectorListForm = withUserRoleRequired(AppPages.MarketSectorView, { displayList: true });
  const ProtectedCreateMarketSectorForm = withUserRoleRequired(AppPages.MarketSectorView, { displayList: false });
  const ProtectedGrossBranchListForm = withUserRoleRequired(AppPages.GrossBranchView, { displayList: true });
  const ProtectedCreateGrossBranchForm = withUserRoleRequired(AppPages.GrossBranchView, { displayList: false });
  const ProtectedBranchCostConfigListForm = withUserRoleRequired(AppPages.BranchCostConfigView, { displayList: true });
  const ProtectedCreateBranchCostConfigForm = withUserRoleRequired(AppPages.BranchCostConfigView, { displayList: false });
  const ProtectedPricingSettingsListForm = withUserRoleRequired(AppPages.PricingSettingsView, { displayList: true });
  const ProtectedCreatePricingSettingsForm = withUserRoleRequired(AppPages.PricingSettingsView, { displayList: false });
  const ProtectedPricingConfigurationListForm = withUserRoleRequired(PricingConfigurationListForm);
  const ProtectedQuoteApprovalReassignmentListForm = withEmployeeRequired(QuoteApprovalReassignmentListForm);
  const ProtectedCreateQuoteApprovalReassignmentForm = withEmployeeRequired(CreateQuoteApprovalReassignmentForm);
  const ProtectedAuditListForm = withUserRoleRequired(AppPages.AuditListForm);
  const ProtectedGMLevelListForm = withUserRoleRequired(AppPages.GrossMarginLevelView, { displayList: true });
  const ProtectedCreateGrossMarginLevelForm = withUserRoleRequired(AppPages.GrossMarginLevelView, { displayList: false });
  const ProtectedCreateUserRoleForm = withSuperAdminRoleRequired(CreateUserRoleForm);
  const ProtectedUserRoleListForm = withSuperAdminRoleRequired(UserRoleListForm);
  const ProtectedAuthorisedSignatureConfigListForm = withContractAuthManagerRoleRequired(AuthorisedSignatureConfigListForm);
  const ProtectedCraeteAuthSignConfigForm = withContractAuthManagerRoleRequired(CreateAuthSignConfigForm);
  const ProtectedItineraryTimesListForm = withUserRoleRequired(AppPages.ItineraryTimesView, { displayList: true });
  const ProtectedCreateItineraryTimesForm = withUserRoleRequired(AppPages.ItineraryTimesView, { displayList: false });
  const ProtectedPricingChangesListForm = withEitherUserRoleOrPricingApprover(AppPages.PricingChangesListForm);
  const ProtectedReviewDetailsForm = withEitherUserRoleOrPricingApprover(AppPages.ReviewDetailsForm);

  const routerFromElements = createBrowserRouter(
    createRoutesFromElements(
      <Route>
        <Route path="/" element={user && employee ? <ProtectedHome /> : <SigninForm />} />
        <Route path="/login" element={<ProtectedLoginForm />} />
        <Route path="/quotelist" element={<ProtectedQuoteApprovalListForm />} />
        <Route path="/quoteDetails" element={<ProtectedQuoteDetails />} />
        <Route path="/home" element={<ProtectedHome />} />
        <Route path="/devicelist" element={<ProtectedDeviceListForm />} />
        <Route path="/device" element={<ProtectedCreateDeviceForm />} />
        <Route path="/zonelist" element={<ProtectedZoneListForm />} />
        <Route path="/zone" element={<ProtectedCreateZoneForm />} />
        <Route path="/pezodelist" element={<ProtectedPezodeListForm />} />
        <Route path="/pezode" element={<ProtectedCreatePezodeForm />} />
        <Route path="/devicetypelist" element={<ProtectedDeviceTypeListForm />} />
        <Route path="/devicetype" element={<ProtectedCreateDeviceTypeForm />} />
        <Route path="/consumableproductlist" element={<ProtectedConsumableProductListForm />} />
        <Route path="/consumableproduct" element={<ProtectedCreateConsumableProductForm />} />
        <Route path="/servicetypelist" element={<ProtectedServiceTypeListForm />} />
        <Route path="/servicetype" element={<ProtectedCreateServiceTypeForm />} />
        <Route path="/marketsectorlist" element={<ProtectedMarketSectorListForm />} />
        <Route path="/marketsector" element={<ProtectedCreateMarketSectorForm />} />
        <Route path="/grossbranchlist" element={<ProtectedGrossBranchListForm />} />
        <Route path="/grossbranch" element={<ProtectedCreateGrossBranchForm />} />
        <Route path="/branchcostconfiglist" element={<ProtectedBranchCostConfigListForm />} />
        <Route path="/branchcostconfig" element={<ProtectedCreateBranchCostConfigForm />} />
        <Route path="/pricingsettingslist" element={<ProtectedPricingSettingsListForm />} />
        <Route path="/pricingsettings" element={<ProtectedCreatePricingSettingsForm />} />
        <Route path="/pricingconfiguration" element={<ProtectedPricingConfigurationListForm />} />
        <Route path="/quoteapprovalreassignmentlist" element={<ProtectedQuoteApprovalReassignmentListForm />} />
        <Route path="/quoteapprovalreassignment" element={<ProtectedCreateQuoteApprovalReassignmentForm />} />
        <Route path="/auditinfo" element={<ProtectedAuditListForm />} />
        <Route path="/:gmtype" element={<ProtectedGMLevelListForm />} />
        <Route path="/userrole" element={<ProtectedCreateUserRoleForm />} />
        <Route path="/userrolelist" element={<ProtectedUserRoleListForm />} />
        <Route path="/authsignconfiglist" element={<ProtectedAuthorisedSignatureConfigListForm />} />
        <Route path="/authsignconfig" element={<ProtectedCraeteAuthSignConfigForm />} />
        <Route path="/grossmarginlevel" element={<ProtectedCreateGrossMarginLevelForm />} />
        <Route path="/itinerarytimeslist" element={<ProtectedItineraryTimesListForm />} />
        <Route path="/itinerarytimes" element={<ProtectedCreateItineraryTimesForm />} />
        <Route path="/pricingchanges" element={<ProtectedPricingChangesListForm />} />
        <Route path="/reviewdetails" element={<ProtectedReviewDetailsForm />} />
      </Route>
    )
  );

  return <RouterProvider router={routerFromElements} />;
};

export { PageRoute };
